import { ArrowLeft, CalendarDays, FileCheck2, MoreHorizontal, Printer, Stethoscope } from "lucide-react";
import { PROXIMOS_STATUS, type StatusDoPlano } from "@bookdental/schemas";
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { SeloDoStatus } from "./SeloDoStatus";

const ACAO: Partial<Record<StatusDoPlano, string>> = {
  APROVADO: "Aprovar",
  RECUSADO: "Recusado pelo paciente",
  CONCLUIDO: "Concluir",
  CANCELADO: "Cancelar plano",
  RASCUNHO: "Reabrir",
};
/** A ação que fica à vista; o resto vai pro "⋯" (recusar e cancelar não competem com aprovar). */
const PRINCIPAL: StatusDoPlano[] = ["APROVADO", "CONCLUIDO", "RASCUNHO"];
const data = (d: Date | string) => new Date(d).toLocaleDateString("pt-BR");

/**
 * Cabeçalho do plano em três níveis: voltar; identidade (número, título, situação) com as ações à
 * direita; e a linha de detalhes (dentista responsável, criação, entrega do orçamento).
 */
export function CabecalhoDoPlano({
  numero,
  titulo,
  criadoEm,
  status,
  entregueEm,
  dentistaId,
  podeMontar,
  temItens,
  onVoltar,
  onImprimir,
  onStatus,
  onDentista,
}: {
  numero: number | null;
  titulo: string | null;
  criadoEm: Date | string | null;
  status: StatusDoPlano;
  entregueEm: Date | string | null;
  dentistaId: string | null;
  podeMontar: boolean;
  temItens: boolean;
  onVoltar: () => void;
  onImprimir: () => void;
  onStatus: (s: StatusDoPlano) => void;
  onDentista: (id: string | null) => void;
}) {
  const dentistas = trpc.organization.listDentists.useQuery({ apenasAtivos: true });
  const proximos = numero == null ? [] : PROXIMOS_STATUS[status];
  const principais = proximos.filter((s) => PRINCIPAL.includes(s));
  const secundarias = proximos.filter((s) => !PRINCIPAL.includes(s));
  const detalhe = "inline-flex items-center gap-1.5 text-[12.5px] text-zinc-500 dark:text-muted-foreground";

  return (
    <div className="space-y-3 border-b border-zinc-200 pb-4 dark:border-border">
      <button type="button" onClick={onVoltar} className="inline-flex items-center gap-1 text-xs font-medium text-zinc-500 hover:text-zinc-800">
        <ArrowLeft className="h-3.5 w-3.5" /> Planos de tratamento
      </button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-xl font-semibold text-zinc-900 dark:text-foreground">{numero == null ? "Novo plano" : `Plano nº ${numero}`}</h2>
            <SeloDoStatus status={status} />
          </div>
          {titulo && <p className="mt-0.5 text-sm text-zinc-600 dark:text-muted-foreground">{titulo}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {numero != null && (
            <Button variant="outline" size="sm" onClick={onImprimir}>
              <Printer className="mr-1.5 h-3.5 w-3.5" /> Imprimir orçamento
            </Button>
          )}
          {podeMontar &&
            principais.map((s) => (
              <Button key={s} size="sm" variant={s === "RASCUNHO" ? "outline" : "default"} disabled={!temItens && s === "APROVADO"} onClick={() => onStatus(s)}>
                {ACAO[s]}
              </Button>
            ))}
          {podeMontar && secundarias.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" aria-label="Mais ações do plano" className="px-2">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="border border-zinc-200 bg-white shadow-lg dark:border-border dark:bg-card">
                {secundarias.map((s) => (
                  <DropdownMenuItem key={s} onClick={() => onStatus(s)} className="text-red-700">
                    {ACAO[s]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <label className={detalhe}>
          <Stethoscope className="h-3.5 w-3.5" />
          Dentista responsável
          <select
            aria-label="Dentista responsável"
            value={dentistaId ?? ""}
            disabled={!podeMontar}
            onChange={(e) => onDentista(e.target.value || null)}
            className="h-7 rounded-md border border-zinc-200 bg-white px-1.5 text-[12.5px] font-medium text-zinc-900 disabled:bg-transparent disabled:border-transparent dark:border-border dark:bg-card dark:text-foreground"
          >
            <option value="">Escolher</option>
            {(dentistas.data ?? []).map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </label>
        {criadoEm && (
          <span className={detalhe}>
            <CalendarDays className="h-3.5 w-3.5" /> Criado em {data(criadoEm)}
          </span>
        )}
        {entregueEm && (
          <span className={detalhe}>
            <FileCheck2 className="h-3.5 w-3.5" /> Orçamento entregue em {data(entregueEm)}
          </span>
        )}
      </div>
    </div>
  );
}
